import { createContext, useContext } from "react";

/**
 * UserContext provides the current user's identity and permissions
 * to all components in the gateway tree. Populated by GatewayApp after
 * getMe() resolves.
 *
 * Shape:
 *   {
 *     user_id: "abc-123",
 *     username: "warren",
 *     role: "admin" | "engineer" | "viewer",
 *     permissions: ["read", "write", "configure", "admin"]
 *   }
 *
 * Usage:
 *   const user = useUser();
 *   const canConfigure = usePermission("configure");
 *   <button disabled={!canConfigure}>Save</button>
 */

const UserContext = createContext(null);

export const UserProvider = UserContext.Provider;

export function useUser() {
  return useContext(UserContext);
}

/**
 * Returns true if the current user has the given permission.
 * Returns false until the user object is loaded.
 */
export function usePermission(permission) {
  const user = useContext(UserContext);
  if (!user) return false;
  return (user.permissions || []).includes(permission);
}
